import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PROVIDERS,
  deleteIntegration,
  listIntegrations,
  setIntegration,
  type ProviderId,
} from './integrations.api';
import { readApiError } from '../../lib/api';

export function IntegrationsPanel() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['integrations'], queryFn: listIntegrations });

  const [error, setError] = useState<string | null>(null);

  const setMut = useMutation({
    mutationFn: (vars: { provider: ProviderId; key: string; value: string }) =>
      setIntegration(vars.provider, vars.key, vars.value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
    onError: (err) => setError(readApiError(err).message),
  });

  const delMut = useMutation({
    mutationFn: (vars: { provider: ProviderId; key: string }) =>
      deleteIntegration(vars.provider, vars.key),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  });

  return (
    <div>
      <h2>Integraciones</h2>
      <p style={{ color: 'var(--color-muted)' }}>
        Las credenciales se cifran con AES-256-GCM y se guardan localmente. El proveedor activo se
        define con las variables de entorno <code>WHATSAPP_PROVIDER</code> y{' '}
        <code>DIAN_PROVIDER</code>.
      </p>

      {error && <p className="error">{error}</p>}

      {PROVIDERS.map((p) => (
        <ProviderCard
          key={p.id}
          provider={p}
          configured={list.data?.[p.id] ?? []}
          onSet={(key, value) => setMut.mutate({ provider: p.id, key, value })}
          onDelete={(key) => delMut.mutate({ provider: p.id, key })}
          submitting={setMut.isPending}
        />
      ))}
    </div>
  );
}

function ProviderCard(props: {
  provider: { id: ProviderId; label: string; keys: readonly string[] };
  configured: string[];
  onSet: (key: string, value: string) => void;
  onDelete: (key: string) => void;
  submitting: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [value, setValue] = useState('');

  const allConfigured = props.provider.keys.every((k) => props.configured.includes(k));

  return (
    <div className="card" style={{ marginBottom: '0.75rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          cursor: 'pointer',
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: allConfigured ? 'var(--color-accent)' : '#cbd5e1',
          }}
        />
        <strong>{props.provider.label}</strong>
        <span style={{ color: 'var(--color-muted)', fontSize: '0.85rem' }}>
          {props.configured.length}/{props.provider.keys.length} credenciales
        </span>
        <span style={{ marginLeft: 'auto', color: 'var(--color-muted)' }}>
          {open ? '▴' : '▾'}
        </span>
      </div>

      {open && (
        <div style={{ marginTop: '0.75rem' }}>
          {props.provider.keys.map((k) => {
            const isSet = props.configured.includes(k);
            const isEditing = editing === k;
            return (
              <div
                key={k}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  borderTop: '1px solid var(--color-border)',
                  padding: '0.6rem 0',
                }}
              >
                <code style={{ minWidth: 130 }}>{k}</code>
                {!isEditing ? (
                  <>
                    <span style={{ color: isSet ? 'var(--color-accent)' : 'var(--color-muted)' }}>
                      {isSet ? '••••••••' : 'no configurado'}
                    </span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
                      <button
                        className="btn"
                        onClick={() => {
                          setEditing(k);
                          setValue('');
                        }}
                      >
                        {isSet ? 'Cambiar' : 'Definir'}
                      </button>
                      {isSet && (
                        <button className="btn" onClick={() => props.onDelete(k)}>
                          Eliminar
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <input
                      className="input"
                      type="password"
                      autoFocus
                      placeholder={`Nuevo valor para ${k}`}
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button
                      className="btn btn-primary"
                      disabled={!value || props.submitting}
                      onClick={() => {
                        props.onSet(k, value);
                        setEditing(null);
                        setValue('');
                      }}
                    >
                      Guardar
                    </button>
                    <button className="btn" onClick={() => setEditing(null)}>
                      Cancelar
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
